/**
 * Integrytas Group - Common Security Module (CSM)
 * Generic admin page for org-lookup tables.
 *
 * One file drives Company, Division, Business Unit, and Office: each
 * follows the same master + _tl pattern with a single-default flag, so we
 * configure the table/column names through a `OrgLookupConfig` prop.
 *
 * Visual + behavioral parity with ApprovalHierarchyPage:
 *   - sortable list grid with sticky header
 *   - persistent text-search filter (sessionStorage)
 *   - Edit + Delete in row Actions (Delete blocked when in-use anywhere)
 *   - Right-side edit drawer with translations editor
 *   - Single-default UI checkbox; DB trigger enforces at-most-one
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveLocale } from '../../lib/use_active_locale';
import { apiDb } from '../../lib/api_client';
import { fetchAllRows } from '../../lib/fetch_all_rows';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';

const ORG_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code', type: 'text',    label: 'Internal Code', required: true, col_span: 1 },
  { key: 'is_active',     type: 'boolean', label: 'Active',        col_span: 1 },
  { key: 'is_default',    type: 'boolean', label: 'Default',       col_span: 1 },
];
const ORG_CORE_LAYOUT = toUldCoreLayout(ORG_FIELD_DEFS);

export interface OrgLookupConfig {
  table: string;             // 'csm_company'
  tlTable: string;           // 'csm_company_tl'
  pkColumn: string;           // 'company_id'
  storageKey: string;         // 'csm.companies'
  labelSingular: string;      // 'Company'
  labelPlural: string;        // 'Companies'
  /**
   * Column on csm_user_master that references this lookup (e.g. 'company_id').
   * Used by Delete to refuse when any user still references the row.
   */
  userMasterColumn: string;
  /**
   * Personalization page code (e.g. 'CSM.ORGLOOKUP.COMPANY').
   */
  pageCode: string;
}

export interface OrgLookupPageProps {
  session: AuthSession;
  config: OrgLookupConfig;
}

type SortKey = 'internal_code' | 'display_text' | 'is_default' | 'is_active';

interface Row {
  pk: number;
  internal_code: string;
  is_default: 'Y' | 'N';
  is_active: 'Y' | 'N';
  display_text: string;
}

interface TLRow {
  locale_code: string;
  display_text: string;
  description: string;
}

/**
 * A user assigned to this org row (expand/contract "related children" panel).
 * display_name is the resolved label; email is the natural identifier shown as
 * hover text (§17.B4 — never a raw surrogate id).
 */
interface UserRef {
  user_id: number;
  display_name: string;
  email: string;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',    defaultWidth: 160 },
  { key: 'display_text',  header: 'Name',    defaultWidth: 200 },
  { key: 'is_default',    header: 'Default', defaultWidth: 90  },
  { key: 'is_active',     header: 'Active',  defaultWidth: 80  },
];

export function OrgLookupPage({ session, config }: OrgLookupPageProps) {
  return (
    <PersonalizationProvider pageCode={config.pageCode} session={session}>
      <OrgLookupPageInner session={session} config={config} />
    </PersonalizationProvider>
  );
}

function OrgLookupPageInner({ session, config }: OrgLookupPageProps) {
  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  // Related-children (expand/contract detail panel) row-expansion state, keyed by pk.
  const detail = useExpandedRows<number>();
  const [rows, setRows]     = useState<Row[]>([]);
  // Users assigned to each org row (via the entity's FK on csm_user_master),
  // grouped by parent pk for the side-by-side "Users" card.
  const [usersByOrg, setUsersByOrg] = useState<Map<number, UserRef[]>>(new Map());
  const [filter, setFilter] = useState(() => sessionStorage.getItem(config.storageKey + '.filter') ?? '');
  // §17.B5: per-column multi-value facets for the list's logical columns.
  const [facets, setFacets] = useState<{ is_default: string[]; is_active: string[] }>({ is_default: [], is_active: ['Y'] });
  const [editing, setEditing]   = useState<Row | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const sort = useSort<SortKey>(config.storageKey + '.sort', { key: 'internal_code', dir: 'asc' });

  useEffect(() => {
    sessionStorage.setItem(config.storageKey + '.filter', filter);
  }, [filter, config.storageKey]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const locale = activeLocale;
      const { data, error: e } = await apiDb
        .from(config.table)
        .select(`${config.pkColumn}, internal_code, is_default, is_active, ${config.tlTable} ( locale_code, display_text )`)
        .order('internal_code');
      if (e) throw e;
      const list: Row[] = (data ?? []).map((r: Record<string, unknown>) => {
        const tls: { locale_code: string; display_text: string }[] =
          (r[config.tlTable] as { locale_code: string; display_text: string }[]) ?? [];
        const exact = tls.find(t => t.locale_code === locale);
        const fb    = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
        return {
          pk: r[config.pkColumn] as number,
          // Keep the real pk column on the row so useUdcGrid can key ext_attributes by it.
          [config.pkColumn]: r[config.pkColumn] as number,
          internal_code: r.internal_code as string,
          is_default: r.is_default as 'Y' | 'N',
          is_active: r.is_active as 'Y' | 'N',
          display_text: exact?.display_text ?? fb?.display_text ?? (r.internal_code as string),
        } as Row;
      });
      setRows(list);

      // ── Related children: users assigned to each org row ────────────────────
      // The active entity's FK on csm_user_master is config.userMasterColumn
      // (company_id / division_id / business_unit_id / office_id). Group users by
      // that FK so each parent row can reveal its assigned users side-by-side.
      const userData = await fetchAllRows((from, to) => apiDb
        .from('csm_user_master')
        .select(`user_id, display_name, email, ${config.userMasterColumn}`)
        .order('user_id').range(from, to));
      const uMap = new Map<number, UserRef[]>();
      for (const u of (userData ?? []) as Record<string, unknown>[]) {
        const orgId = u[config.userMasterColumn] as number | null;
        if (orgId == null) continue;
        const arr = uMap.get(orgId) ?? [];
        arr.push({
          user_id: u.user_id as number,
          display_name: (u.display_name as string | null) ?? '',
          email: (u.email as string | null) ?? '',
        });
        uMap.set(orgId, arr);
      }
      for (const arr of uMap.values()) {
        arr.sort((a, b) => (a.display_name || a.email).localeCompare(b.display_name || b.email));
      }
      setUsersByOrg(uMap);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // reload is re-created each render; the effect must run on mount and when the
  // selected table changes — and re-resolve _tl display text on a runtime locale
  // switch (§8). reload itself is intentionally not a dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload();   }, [config.table, activeLocale]);

  // User-defined columns / facets an admin placed on this entity's page (runtime,
  // no code). Driven by the active config so each of the four entities (company /
  // division / business_unit / office) is wired through this one hook.
  const udcGrid = useUdcGrid(config.table, config.pageCode, { pkColumn: config.pkColumn, rows: rows as unknown as Record<string, unknown>[] });

  // Distinct facet options derived from the loaded rows (§17.B5). Default / Active
  // are Y/N flags, so the option set is fixed.
  const facetOptions = useMemo(() => ({
    is_default: [{ value: 'Y', label: t('common.yes', 'Yes') }, { value: 'N', label: t('common.no', 'No') }],
    is_active:  [{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }],
  }), [t]);

  const facetsActive = facets.is_default.length + facets.is_active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ is_default: [], is_active: [] }); };

  const filtered = useMemo(() => {
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const matchesFacets = (r: Row) =>
      (facets.is_default.length === 0 || facets.is_default.includes(r.is_default)) &&
      (facets.is_active.length  === 0 || facets.is_active.includes(r.is_active));
    const base = (!filter.trim()
      ? rows
      : rows.filter(r => {
          const q = filter.toLowerCase();
          return r.internal_code.toLowerCase().includes(q) ||
            r.display_text.toLowerCase().includes(q);
        }))
      .filter(matchesFacets)
      .filter(r => udcGrid.matchRow(r as unknown as Record<string, unknown>));
    return sort.sorted(base, (a, b, key) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      return compareStrings(a[key], b[key]);
    });
  }, [rows, filter, facets, sort, udcGrid]);

  const handleDelete = async (r: Row): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      // Refuse if any user still references this row.
      const { count: userCount, error: uErr } = await apiDb
        .from('csm_user_master')
        .select('user_id', { head: true, count: 'exact' })
        .eq(config.userMasterColumn, r.pk);
      if (uErr) throw uErr;
      if ((userCount ?? 0) > 0) {
        throw new Error(
          userCount === 1
            ? t('org_lookup.list.errors.users_reference_one', { count: userCount ?? undefined, label: config.labelSingular })
            : t('org_lookup.list.errors.users_reference_other', { count: userCount ?? undefined, label: config.labelSingular }),
        );
      }
      const { error: dErr } = await apiDb
        .from(config.table)
        .delete()
        .eq(config.pkColumn, r.pk);
      if (dErr) throw dErr;
      await reload();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const hasRelated = (id: number) => (usersByOrg.get(id)?.length ?? 0) > 0;

  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'users',
      table: 'csm_user_master', pkColumn: 'user_id',
      title: t('org.lookup.related.users', 'Users'),
      items: (usersByOrg.get(id) ?? []).map(u => ({
        id: u.user_id,
        label: u.display_name || u.email,
        title: u.email,
      })),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={config.labelPlural}
          description={t('org_lookup.list.description', 'Reference values for {{label}}, with their default and active status.', { label: config.labelPlural })}
          actions={
            <>
              <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              <button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('org_lookup.list.new_item', { label: config.labelSingular })}
              </button>
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('org_lookup.stat.total', 'Total') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('org_lookup.list.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder={t('org_lookup.list.search_placeholder')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('org_lookup.list.columns.default', 'Default')} options={facetOptions.is_default} selected={facets.is_default} onChange={v => setFacets(f => ({ ...f, is_default: v }))} />
            <FacetFilter label={t('org_lookup.list.columns.active', 'Active')} options={facetOptions.is_active} selected={facets.is_active} onChange={v => setFacets(f => ({ ...f, is_active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('org_lookup.list.personalization_banner', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('org.lookup.related.header', 'Related records')}></th>
                  {visibleCols.map(col => (
                    <SortableHeader<SortKey> key={col.key}
                      sortKey={col.key as SortKey} label={col.header}
                      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                      style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('org_lookup.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.pk}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.pk)}
                        hasChildren={hasRelated(r.pk)}
                        onToggle={() => detail.toggle(r.pk)}
                        expandLabel={t('org.lookup.related.expand_aria', 'Show related records')}
                        collapseLabel={t('org.lookup.related.collapse_aria', 'Hide related')}
                        tooltip={t('org.lookup.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>}
                        {col.key === 'display_text'  && r.display_text}
                        {col.key === 'is_default'    && <YesNoBadge value={r.is_default} yesLabel={t('org_lookup.list.columns.default', 'Default')} noLabel={t('common.standard', 'Standard')} />}
                        {col.key === 'is_active'     && <YesNoBadge value={r.is_active}  yesLabel={t('common.boolean.active', 'Active')}  noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                        className="mr-1 inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft"
                      >{t('org_lookup.list.actions.edit')}</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}
                        disabled={r.is_default === 'Y'}
                        title={r.is_default === 'Y' ? t('org_lookup.list.actions.delete_disabled_tooltip', { label: config.labelSingular }) : t('org_lookup.list.actions.delete_tooltip', { label: config.labelSingular })}
                        className="inline-block min-w-[3.75rem] rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:bg-csm-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                      >{t('org_lookup.list.actions.delete')}</button>
                    </td>
                  </tr>
                  {detail.isOpen(r.pk) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.pk)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState
                      variant={anyFilterActive ? 'no-match' : 'no-data'}
                      title={t('org_lookup.list.empty', { labelPlural: config.labelPlural })}
                      action={
                        <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                          {t('org_lookup.list.new_item_plus', '+ New {{label}}', { label: config.labelSingular })}
                        </button>
                      }
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <OrgLookupEditModal
          session={session}
          config={config}
          row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('org_lookup.list.delete_confirm_title', 'Delete {{label}}?', { label: config.labelSingular })}
          message={t('org_lookup.list.delete_confirm_message', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.display_text || deleteTarget.internal_code })}
          onConfirm={async () => { const ok = await handleDelete(deleteTarget); if (ok) setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// EDIT DRAWER
// =============================================================================

interface OrgLookupEditModalProps {
  session: AuthSession;
  config: OrgLookupConfig;
  row: Row | null;
  onClose: () => void;
  onSaved: () => void;
}

function OrgLookupEditModal({ session, config, row, onClose, onSaved }: OrgLookupEditModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer';
  const isCreate = row === null;

  const initialValues = {
    internalCode: row?.internal_code ?? '',
    isActive: row ? row.is_active === 'Y' : true,
    isDefault: row ? row.is_default === 'Y' : false,
  };
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  // Custom fields placed on this entity (host = the active config's page) become
  // first-class FieldRenderer items: they interleave with native fields and inherit
  // the same per-tier personalization (hide / read-only / required / reorder). Driven
  // by the active config so each of the four entities is wired through this one hook.
  const udcForm = useUdcFormFields(config.table, config.pageCode, { pkColumn: config.pkColumn, pk: row?.pk });
  const orgFieldDefs = useMemo(() => [...ORG_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const orgCoreLayout = useMemo(() => [...ORG_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const { layout } = useUldLayout('CSM.ORGLOOKUP.MODAL', orgCoreLayout);
  const [translations, setTranslations] = useState<TLRow[]>([]);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.pk, isCreate]);

  useEffect(() => {
    if (isCreate || !row) {
      setTranslations([{ locale_code: 'EN_US', display_text: '', description: '' }]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await apiDb
        .from(config.tlTable)
        .select('locale_code, display_text, description')
        .eq(config.pkColumn, row.pk);
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      const list: TLRow[] = (data ?? []).map((t: TlRow) => ({
        locale_code: t.locale_code, display_text: t.display_text ?? '', description: t.description ?? '',
      }));
      setTranslations(list.length > 0 ? list : [{ locale_code: 'EN_US', display_text: '', description: '' }]);
    })();
    return () => { cancelled = true; };
    // Keyed on row?.pk (not the whole `row` object): translations reload only when
    // the selected row's identity changes, not on unrelated row object churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, row?.pk, config.tlTable, config.pkColumn]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const actorId = getForensicActorId(session);
      let pk = row?.pk ?? null;

      if (isCreate) {
        const { data, error: e } = await apiDb
          .from(config.table)
          .insert({
            internal_code: form.internalCode,
            is_default: form.isDefault ? 'Y' : 'N',
            is_active:  form.isActive  ? 'Y' : 'N',
            modified_by: actorId,
          })
          .select(config.pkColumn).single();
        if (e) throw e;
        pk = (data as Record<string, number>)[config.pkColumn];
      } else {
        const { error: e } = await apiDb
          .from(config.table)
          .update({
            is_default: form.isDefault ? 'Y' : 'N',
            is_active:  form.isActive  ? 'Y' : 'N',
            modified_by: actorId,
          })
          .eq(config.pkColumn, row?.pk);
        if (e) throw e;
      }

      for (const t of translations) {
        if (!t.locale_code || !t.display_text) continue;
        const { error: e } = await apiDb
          .from(config.tlTable)
          .upsert({
            [config.pkColumn]: pk,
            locale_code: t.locale_code,
            display_text: t.display_text,
            description: t.description || null,
          }, { onConflict: `${config.pkColumn},locale_code` });
        if (e) throw e;
      }

      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const modalTitle = isCreate
    ? t('org_lookup.drawer.create_title', 'Create {{label}}', { label: config.labelSingular })
    : t('org_lookup.drawer.edit_title', 'Edit {{label}} — {{code}}', { label: config.labelSingular, code: row?.internal_code ?? '' });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        headerAction={<>{session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) && null}<PersonalizationToggle /></>}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            <button onClick={() => void save()} disabled={busy || (isCreate && !form.internalCode)}
                    className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        {isPersonalizing && (
          <div className="-mx-4 -mt-4 mb-4 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
            {t('org_lookup.drawer.personalization_banner', 'Toggle fields — hide or show. Click icon to save.')}
          </div>
        )}
        <div className="mb-3">
          <FieldRenderer
            fields={orgFieldDefs}
            layout={layout}
            columns={3}
            pageCode="CSM.ORGLOOKUP.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'internal_code') return (
                <PersonalizedField componentKey={CK} fieldKey="internal_code" label={t('org_lookup.drawer.field.internal_code')}>
                  <div>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('org_lookup.drawer.field.internal_code')}</label>
                    <input
                      type="text" value={form.internalCode} disabled={!isCreate}
                      onChange={e => setForm(f2 => ({ ...f2, internalCode: e.target.value.toUpperCase() }))}
                      onBlur={() => {
                        if (isCreate && form.internalCode && !/^[A-Z][A-Z0-9_.]*$/.test(form.internalCode)) {
                          setInternalCodeError(t('org_lookup.drawer.field.internal_code_error', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                        } else {
                          setInternalCodeError(null);
                        }
                      }}
                      placeholder={t('org_lookup.drawer.field.internal_code_placeholder')}
                      className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted"
                    />
                    {internalCodeError
                      ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                      : isCreate && <p className="text-csm-xs text-csm-ink-soft mt-1">{t('org_lookup.drawer.field.internal_code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                    }
                  </div>
                </PersonalizedField>
              );
              if (f.key === 'is_active') return (
                <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('org_lookup.drawer.field.active_checkbox')}>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
                      <input type="checkbox" checked={form.isActive} onChange={e => setForm(f2 => ({ ...f2, isActive: e.target.checked }))} className="h-4 w-4 accent-gray-700" />
                      {t('org_lookup.drawer.field.active_checkbox')}
                    </label>
                  </div>
                </PersonalizedField>
              );
              if (f.key === 'is_default') return (
                <PersonalizedField componentKey={CK} fieldKey="is_default" label={t('org_lookup.drawer.field.use_as_default')}>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
                      <input type="checkbox" checked={form.isDefault} onChange={e => setForm(f2 => ({ ...f2, isDefault: e.target.checked }))} className="h-4 w-4 accent-gray-700" />
                      {t('org_lookup.drawer.field.use_as_default')}
                    </label>
                  </div>
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey={CK} fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>
        {form.isDefault && (
          <div className="mb-3 rounded border border-csm-info-border bg-csm-info-soft px-2 py-1 text-csm-xs text-csm-info">
            {t('org_lookup.drawer.hints.default_warning', { label: config.labelSingular })}
          </div>
        )}

        <PersonalizedField componentKey={CK} fieldKey="translations" label={t('org_lookup.drawer.translations.label')}>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-csm-sm font-medium text-csm-ink-soft">{t('org_lookup.drawer.translations.label')}</label>
              <button type="button" onClick={() => setTranslations(prev => [...prev, { locale_code: '', display_text: '', description: '' }])}
                      className="text-csm-xs text-csm-ink-soft hover:underline">{t('org_lookup.drawer.translations.add_locale')}</button>
            </div>
            <div className="overflow-hidden rounded border border-csm-border">
              <table className="w-full text-csm-sm">
                <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                  <tr>
                    <th className="px-2 py-1 text-left">{t('org_lookup.drawer.translations.columns.locale')}</th>
                    <th className="px-2 py-1 text-left">{t('org_lookup.drawer.translations.columns.display_text')}</th>
                    <th className="px-2 py-1 text-left">{t('org_lookup.drawer.translations.columns.description')}</th>
                    <th className="w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {translations.map((tl, i) => (
                    <tr key={i} className="border-t border-csm-border-soft">
                      <td className="px-1 py-1">
                        <input value={tl.locale_code} onChange={e => {
                          const v = e.target.value.toUpperCase();
                          setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, locale_code: v } : row));
                        }} placeholder={t('org_lookup.drawer.translations.locale_placeholder')} className="h-6 w-20 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={tl.display_text} onChange={e => {
                          const v = e.target.value;
                          setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, display_text: v } : row));
                        }} className="h-6 w-full rounded border border-csm-border px-1 text-csm-sm" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={tl.description} onChange={e => {
                          const v = e.target.value;
                          setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, description: v } : row));
                        }} className="h-6 w-full rounded border border-csm-border px-1 text-csm-sm" />
                      </td>
                      <td className="px-1 py-1 text-right">
                        <button type="button" onClick={() => setTranslations(prev => prev.filter((_, idx) => idx !== i))}
                                className="text-csm-watermark hover:text-csm-danger">x</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </PersonalizedField>

        {error && (
          <div className="mt-3">
            <ErrorBanner
              message={t('org_lookup.drawer.save_failed', "Couldn't save the record.")}
              detail={error}
              details_label={t('common.details', 'Details')}
            />
          </div>
        )}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('common.discard_title', 'Discard changes?')}
          message={t('common.discard_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('common.discard_confirm', 'Discard')}
          cancelLabel={t('common.discard_cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// =============================================================================
// FOUR PRECONFIGURED COMPONENTS for direct registration in PAGE_COMPONENTS.
// =============================================================================

const COMPANY_CONFIG: OrgLookupConfig = {
  table: 'csm_company', tlTable: 'csm_company_tl', pkColumn: 'company_id',
  storageKey: 'csm.companies', labelSingular: 'Company', labelPlural: 'Companies',
  userMasterColumn: 'company_id',
  pageCode: 'CSM.ORGLOOKUP.COMPANY',
};
const DIVISION_CONFIG: OrgLookupConfig = {
  table: 'csm_division', tlTable: 'csm_division_tl', pkColumn: 'division_id',
  storageKey: 'csm.divisions', labelSingular: 'Division', labelPlural: 'Divisions',
  userMasterColumn: 'division_id',
  pageCode: 'CSM.ORGLOOKUP.DIVISION',
};
const BUSINESS_UNIT_CONFIG: OrgLookupConfig = {
  table: 'csm_business_unit', tlTable: 'csm_business_unit_tl', pkColumn: 'business_unit_id',
  storageKey: 'csm.business-units', labelSingular: 'Business Unit', labelPlural: 'Business Units',
  userMasterColumn: 'business_unit_id',
  pageCode: 'CSM.ORGLOOKUP.BUSINESS_UNIT',
};
const OFFICE_CONFIG: OrgLookupConfig = {
  table: 'csm_office', tlTable: 'csm_office_tl', pkColumn: 'office_id',
  storageKey: 'csm.offices', labelSingular: 'Office', labelPlural: 'Offices',
  userMasterColumn: 'office_id',
  pageCode: 'CSM.ORGLOOKUP.OFFICE',
};

// Self-register as UDC hosts (auto-discovered candidate pages for these entities).
for (const c of [COMPANY_CONFIG, DIVISION_CONFIG, BUSINESS_UNIT_CONFIG, OFFICE_CONFIG]) {
  registerUdcHost(c.pageCode, c.table, c.labelPlural);
}

export const CompanyPage      = (props: { session: AuthSession }) => <OrgLookupPage {...props} config={COMPANY_CONFIG} />;
export const DivisionPage     = (props: { session: AuthSession }) => <OrgLookupPage {...props} config={DIVISION_CONFIG} />;
export const BusinessUnitPage = (props: { session: AuthSession }) => <OrgLookupPage {...props} config={BUSINESS_UNIT_CONFIG} />;
export const OfficePage       = (props: { session: AuthSession }) => <OrgLookupPage {...props} config={OFFICE_CONFIG} />;
